// src/hooks/usePrefetch.js
import { useEffect, useRef } from 'react';

/**
 * Hook to prefetch components or resources when an element enters the viewport
 * 
 * @param {Function} prefetchFn - Function to call when element is visible
 * @param {Object} options - Intersection Observer options
 * @returns {Object} ref - Attach this ref to the element you want to observe
 */
export const usePrefetch = (prefetchFn, options = {}) => {
  const elementRef = useRef(null);
  const prefetchedRef = useRef(false);
  
  useEffect(() => {
    // Skip if already prefetched or no element to observe
    if (prefetchedRef.current || !elementRef.current) return;
    
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        // When element becomes visible
        if (entry.isIntersecting && !prefetchedRef.current) {
          // Execute the prefetch function
          prefetchFn();
          
          // Mark as prefetched to avoid duplicate prefetching
          prefetchedRef.current = true;
          
          // Stop observing once prefetched
          observer.disconnect();
        }
      });
    }, {
      rootMargin: '200px 0px', // Start prefetching when within 200px of viewport
      threshold: 0.1,
      ...options
    });
    
    // Start observing the element
    observer.observe(elementRef.current);
    
    return () => {
      observer.disconnect();
    };
  }, [prefetchFn, options]);
  
  return elementRef;
};

/**
 * Creates a prefetcher function for React components loaded with React.lazy()
 * 
 * @param {Function} importFn - Dynamic import function, e.g. () => import('./Page')
 * @returns {Function} Prefetch function
 */
export const createComponentPrefetcher = (importFn) => {
  return () => {
    try {
      // Execute the import but don't await the result
      // This triggers the webpack loader to fetch the chunk
      importFn();
    } catch (err) {
      // Silently fail prefetching - it's just an optimization
      console.debug('Failed to prefetch component:', err);
    }
  };
};

export default usePrefetch;